// src/components/ContractorMaintViewPage/ContractorMaintViewPage.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import { format } from "date-fns";
import PortalSidebar from "../PortalSidebar/PortalSidebar";

// Base URL for all API calls
const API_BASE = "https://portal.gmacc.co.za/api";

// ─── Pagination Component ─────────────────────────────────────────────────────
const Pagination = ({ page, total, perPage, onChange }) => {
  const first = (page - 1) * perPage + 1;
  const last = Math.min(first + perPage - 1, total);
  const pages = Math.ceil(total / perPage);
  return (
    <div className={styles.pagination}>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page - 1)}
        disabled={page === 1 || total === 0}
      >
        Previous
      </button>
      <span className={styles.pageMeta}>
        {total === 0 ? "0–0 of 0" : `${first}–${last} of ${total}`}
      </span>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page + 1)}
        disabled={page === pages || total === 0}
      >
        Next
      </button>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const display = (obj, key) => {
  const fmt = obj?.[`${key}@OData.Community.Display.V1.FormattedValue`];
  if (fmt != null) return fmt;
  if (obj?.[key] != null) return obj[key];
  return "—";
};
const fmtDate = (dt) => {
  if (!dt) return "—";
  try {
    return format(new Date(dt), "dd MMM yyyy");
  } catch {
    return "—";
  }
};

export default function ContractorMaintViewPage() {
  const { id, contractorId } = useParams();
  const navigate = useNavigate();
  const buildingName = localStorage.getItem("buildingName");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

useEffect(() => {
  const handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    setIsDesktop(desktop);

    // Only force closed when moving to smaller screens.
    // On desktop, keep whatever state the user last chose.
    if (!desktop) {
      setSidebarOpen(false);
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);
  useEffect(() => {
  function handleClick(e) {
    // On desktop/laptop, only the burger should control the sidebar
    if (isDesktop) return;

    const sidebar = document.getElementsByClassName(styles.sidebar)[0];
    const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

    if (!sidebar || !hamburger) return;

    const clickedInsideSidebar = sidebar.contains(e.target);
    const clickedHamburger = hamburger.contains(e.target);

    if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
      setSidebarOpen(false);
    }
  }

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [sidebarOpen, isDesktop]);

  // ─── Hooks ───────────────────────────────────────────────────────────────────
  const [job, setJob] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [displayName, setDisplayName] = useState("Name Surname ▾");
  const perPage = 10;
  const [payPage, setPayPage] = useState(1);

  // ─── Fetch Maintenance + Payments ────────────────────────────────────────────
  useEffect(() => {
    document.title = "GMA Portal || Maintenance Detail";
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [jRes, pRes] = await Promise.all([
          fetch(`${API_BASE}/maintenance/${id}/`, { headers }),
          fetch(`${API_BASE}/maintenance/${id}/maintpayments/${contractorId}`, {
            headers,
          }),
        ]);
        if (!jRes.ok || !pRes.ok) throw new Error();
        setJob(await jRes.json());
        const pj = await pRes.json();
        setPayments(Array.isArray(pj) ? pj : pj.value || []);
      } catch {
        setError("Could not load maintenance job.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, contractorId, navigate]);

  // ─── Determine access + user display name ───────────────────────────────────
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("token");
      if (!token) return navigate("/");
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, name, full_name, fullname } = payload || {};
        const prettyName =
          full_name || fullname || name || email || "My Profile";

        setDisplayName(`${prettyName} ▾`);

        const resp = await fetch(`${API_BASE}/contact/determine-access/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email, contact_id }),
        });
        const data = await resp.json();
        if (resp.ok) setUserAccess(data.access || []);
      } catch {
      } finally {
        setLoadingAccess(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <main className={styles.contentArea}>
            <div className={styles.panel}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }
  if (error) {
    return <p className={styles.errorText}>{error}</p>;
  }

  // ─── summaryFields for top strip ─────────────────────────────────────────────
  const summaryFields = [
    ["Job Name", "cj_name"],
    ["Status", "cj_progress"],
    ["Invoice Amount", "cj_invoiceamount"],
    ["Paid Amount", "cj_sumofpayments"],
    ["Remainder Amount", "cr446_remainderamount1"],
    ["Maintenance Contact", "_cj_maintenancecontactperson_value"],
    ["Access Contact", "_cj_accesscontactperson_value"],
    ["Date Quote Accepted", "cj_datewhenquoteaccepted"],
    ["Description", "cj_fulldescription"],
  ];
  const colCount = summaryFields.length;

  return (
    <div className={styles.page}>
      {/* NAVBAR */}
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>
            Maintenance: {display(job, "cj_name")}
          </h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName}</span>
        </div>
      </header>

      {/* ═══ CONTENT ═══ */}
      <div className={styles.shell}>
        <PortalSidebar
  activePortal="contractor"
  activeTab="jobs"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  ticketRole={null}
/>

        <main className={styles.contentArea}>
          <div className={styles.panel}>
            {/* ── JOB DETAILS TABLE (full-width) ── */}
            <div className={styles.paymentHeaderCard}>
              {/* BACK BUTTON */}
              <button
                className={styles.paymentBackBtn}
                onClick={() => navigate(-1)}
              >
                Back
              </button>

              {/* TITLE */}
              <h1 className={styles.paymentHeaderTitle}>JOB DETAILS</h1>

              {/* DIVIDER */}
              <div className={styles.paymentHeaderDivider} />

              {/* HEADER LABELS */}
              <div
                className={styles.paymentHeaderGridAuto}
                style={{ "--col-count": summaryFields.length }}
              >
                {summaryFields.map(([label]) => (
                  <div key={label} className={styles.paymentHeaderLabelItem}>
                    {label}:
                  </div>
                ))}
              </div>

              {/* VALUE PILL */}
              <div className={styles.paymentHeaderValuePill}>
                <div
                  className={styles.paymentHeaderGridAuto}
                  style={{ "--col-count": summaryFields.length }}
                >
                  {summaryFields.map(([_, key]) => (
                    <div key={key} className={styles.paymentHeaderValueItem}>
                      {key.toLowerCase().includes("date")
                        ? fmtDate(job[key])
                        : display(job, key)}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── PAYMENTS ── */}
            <div className={styles.paymentCardGrid}>
              <div className={styles.paymentCard}>
                <h2>Payments</h2>
                <div className={styles.responsiveTableWrapper}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>Payment Number</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments
                        .slice((payPage - 1) * perPage, payPage * perPage)
                        .map((p) => (
                          <tr key={p.cr446_paymentid}>
                            <td>{p.cr446_paymentpk}</td>
                            <td>R {display(p, "cr446_amount")}</td>
                            <td>{display(p, "cj_progress")}</td>
                            <td>{fmtDate(p.cj_paymenttime || p.createdon)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={payPage}
                  total={payments.length}
                  perPage={perPage}
                  onChange={setPayPage}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
